import React, { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import axiosInstance from '../../config/axiosConfig'
import { urlConfig } from '../../config/apiConfig'
import { Container } from '../../components/StyleComponent'
import Statistic from '../../components/icons/Statistic'
import {
    Grid,
    CircularProgress,
    Typography,
    Box,
    ButtonGroup,
    Button,
} from '@mui/material'
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ResponsiveContainer,
} from 'recharts'

export default function NewGraphTrend() {
    const [realtimelog, setRealTimelog] = useState([])
    const [parameter, setParameter] = useState('')
    const [activeTab, setActiveTab] = useState('day')
    const [graphData, setGraphData] = useState({
        min_x: 0,
        max_x: 0,
        min_y: 0,
        max_y: 0,
    })
    const [settingHistoryLog, setSettingHistoryLog] = useState(0)
    const { deviceId } = useParams()
    const [device, setDevice] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [chart, setChart] = useState('Line')
    const [loadingGraph, setLoadingGraph] = useState(true)

    useEffect(() => {
        const fetchDevice = async () => {
            if (!deviceId) return
            try {
                const response = await axiosInstance.get(
                    `${urlConfig.deviceById}/${deviceId}`,
                )
                const data = response.data.message
                setDevice(data)
                setGraphData({
                    min_x: data.min_x,
                    max_x: data.max_x,
                    min_y: data.min_y,
                    max_y: data.max_y,
                })
                setSettingHistoryLog(data.setting_history_log)
                console.log(
                    'Graph data:',
                    data.min_x,
                    data.max_x,
                    data.min_y,
                    data.max_y,
                )
            } catch (err) {
                console.error('Error fetching device data:', err)
                setError('ไม่สามารถโหลดข้อมูลอุปกรณ์ได้')
            } finally {
                setLoading(false)
            }
        }

        fetchDevice()
    }, [deviceId])

    function sortByTime(data) {
        return data.sort((a, b) => {
            return (
                new Date(`1970-01-01T${a.name}Z`) -
                new Date(`1970-01-01T${b.name}Z`)
            )
        })
    }

    const getGraph = useCallback(
        async (mode) => {
            setLoadingGraph(true)
            try {
                const period = activeTab.toLowerCase()
                const response = await axiosInstance.get(
                    `${urlConfig.realtime}/${deviceId}?period=${period}&name_parameter=${parameter}`,
                )
                const data = response.data.message
                const sortedData = sortByTime(data)
                setRealTimelog(sortedData)
                if (data.length > 0 && !parameter && !mode) {
                    setParameter(data[0].name)

                    //ปรับซูม ค่ากราฟให้ดูดี
                }
            } catch (error) {
                console.error('Error fetching real-time graph:', error)
            } finally {
                setLoadingGraph(false)
            }
        },
        [deviceId, activeTab, parameter],
    )

    useEffect(() => {
        getGraph()
    }, [getGraph])

    const getMergedData = () => {
        if (activeTab === 'MONTH' && realtimelog) {
            return dataOfMonths.map((day) => {
                const existingData = realtimelog.find((d) => {
                    const date = new Date(d.name)
                    return date.getDate() === parseInt(day.name, 10)
                })
                return existingData ? { ...existingData, name: day.name } : day
            })
        }
        if (activeTab === 'YEAR' && realtimelog) {
            return dateOfYears.map((day) => {
                const existingData = realtimelog.find((d) => {
                    const date = new Date(d.name)
                    return date.getMonth() + 1 === parseInt(day.name, 10)
                })
                return existingData ? { ...existingData, name: day.name } : day
            })
        }
        return realtimelog
    }

    const graphDataPrepared = getMergedData()
    const dateOfYears = Array.from({ length: 12 }, (_, i) => ({
        name: (i + 1).toString(),
    }))

    const dataOfMonths = Array.from({ length: 31 }, (_, i) => ({
        name: (i + 1).toString(),
    }))

    return (
        <Container
            header={header}
            navigate={{
                title: deviceId,
                navigate: '/graph-trend',
            }}
        >
            {loading ? (
                <LoadingIndicator />
            ) : error ? (
                <ErrorMessage message={error} />
            ) : (
                <DeviceInfo
                    device={device}
                    graphData={graphData}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    realtimelog={realtimelog}
                    chart={chart}
                    setChart={setChart}
                    getGraph={getGraph}
                />
            )}
        </Container>
    )
}

const header = {
    icon: <Statistic sx={{ color: 'white' }} />,
    color: 'grey',
    title: 'Graph Trend',
}

function LoadingIndicator() {
    return (
        <Grid
            container
            justifyContent='center'
            alignItems='center'
            style={{ height: '200px' }}
        >
            <CircularProgress />
        </Grid>
    )
}

function ErrorMessage({ message }) {
    return (
        <Typography color='error' align='center'>
            {message}
        </Typography>
    )
}

function DeviceInfo({
    device,
    graphData,
    activeTab,
    setActiveTab,
    realtimelog,
}) {
    const periodOptions = ['day', 'week', 'month', 'year']

    // เตรียมข้อมูลจาก params ที่ show_graph เป็น true
    const filteredParams = device?.params.filter((param) => param.show_graph)

    // สร้างข้อมูลสำหรับกราฟ
    const graphDataPrepared = realtimelog?.map((log) => {
        const data = {}
        filteredParams.forEach((param) => {
            // สำหรับแต่ละ param ที่ show_graph เป็น true
            data[param.key] = log[param.key] || param.value // ค่า default จาก param
        })
        data.name = log.name
        return data
    })

    return (
        <Grid container spacing={2} alignItems='center' p={3}>
            <Grid item>
                <img
                    src={`${urlConfig.baseUrlAPI}/api/v1${device.image}`}
                    alt='device'
                    width={100}
                    height={100}
                    style={{ borderRadius: 8 }}
                />
            </Grid>
            <Grid item>
                <Box mt={0.5}>
                    <Typography fontSize={14} fontWeight='bold'>
                        {device?.device_type || 'Unknown Device'}
                    </Typography>
                    <Typography fontSize={12}>
                        {device?.device_id
                            ? `ID : ${device.device_id}`
                            : 'Loading...'}
                    </Typography>
                </Box>
            </Grid>

            <Grid item xs={12} mt={3}>
                <Box
                    sx={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid grey',
                    }}
                >
                    <Box
                        display='flex'
                        flexDirection='column'
                        alignItems='center'
                        mt={1}
                        mb={2}
                    >
                        <ButtonGroup
                            variant='contained'
                            aria-label='outlined primary button group'
                            size='small'
                            sx={{
                                boxShadow: 'none',
                                backgroundColor: 'white',
                                color: 'black',
                                borderBottom: '2px solid transparent',
                                borderRadius: '100%',
                            }}
                        >
                            {periodOptions.map((period) => (
                                <Button
                                    variant='contained'
                                    key={period}
                                    onClick={() => setActiveTab(period)}
                                    color={
                                        activeTab === period
                                            ? 'primary'
                                            : 'inherit'
                                    }
                                >
                                    {period.toUpperCase()}
                                </Button>
                            ))}
                        </ButtonGroup>
                    </Box>

                    {/* แสดงกราฟเมื่อมีข้อมูล realtimelog */}
                    <Grid item xs={12} mt={3} alignItems={'center'}>
                        {realtimelog && realtimelog.length > 0 ? (
                            <ResponsiveContainer width='100%' height={300}>
                                <LineChart data={graphDataPrepared}>
                                    <CartesianGrid strokeDasharray='3 3' />
                                    <XAxis
                                        domain={[
                                            graphData.min_x,
                                            graphData.max_x,
                                        ]}
                                        dataKey='name'
                                        tickFormatter={(timestamp) => {
                                            const date = new Date(timestamp)
                                            return `${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}`
                                        }}
                                    />
                                    <YAxis
                                        domain={[
                                            graphData.min_y,
                                            graphData.max_y,
                                        ]}
                                    />
                                    <Tooltip
                                        labelFormatter={(value) => {
                                            const date = new Date(value)
                                            return `${
                                                date.getMonth() + 1
                                            }/${date.getDate()}/${date.getFullYear()}`
                                        }}
                                    />

                                    {filteredParams.map((param) => (
                                        <Line
                                            key={param.parameter_id}
                                            type='monotone'
                                            dataKey={param.key}
                                            stroke='#8884d8'
                                            name={param.name}
                                        />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        ) : (
                            <Typography
                                textAlign='center'
                                mt={4}
                                color='text.secondary'
                            >
                                <CircularProgress />
                            </Typography>
                        )}
                    </Grid>
                </Box>
            </Grid>
        </Grid>
    )
}
